import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { PendingEmployeesDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { useAllEmployeesWithAccess } from 'modules/employees/components/EmployeeCollection/Header/lib/useAllEmployeesWithAccess'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import invitationsCollection from 'modules/employees/store/collections/invitations'

interface Props {
  isHidden: boolean
  onClose: () => void
}

/**
 * Checks if the selection state has any user-applied filters.
 * Returns true if filters are applied (non-empty), false otherwise.
 */
const hasActiveFilters = (selection: EmployeeSelectorV2MultiSelectState): boolean => {
  if (selection.type !== 'all') return false
  const filters = selection.filters
  if (!filters) return false

  // Check if any filter has values
  return Object.values(filters).some(
    (value) => value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0)
  )
}

const PendingModal = ({ isHidden, onClose }: Props) => {
  const [selection, setSelection] = React.useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  // Load all employees for the selector and access IDs for "Select All"
  const { employees, accessIds, loading } = useAllEmployeesWithAccess(PendingEmployeesDocument, {
    skip: isHidden,
  })

  if (isHidden) return null

  const handleClose = () => {
    setSelection({ type: 'manual', ids: [] })
    onClose()
  }

  const handleSubmit = async () => {
    track(trackingEvents.employee.employeesResendInviteFinish)

    let idsToInvite: number[]

    if (selection.type === 'manual') {
      // Manual selection: ids are already access IDs (due to useAccessId prop)
      idsToInvite = selection.ids
    } else if (hasActiveFilters(selection)) {
      // "Select all" WITH filters applied: The EmployeeSelectorV2 returns filter state
      // but the bulk_invite endpoint doesn't support filter-based resolution.
      // This is a known limitation - the user must manually select employees when filters are applied.
      // For now, we prevent this case by disabling "Select All" when filters are active.
      // If this code path is reached, it means the user clicked "Select All" without filters
      // and then applied filters (edge case). In this case, we fall back to excluding only
      // the explicitly excluded IDs.
      const excludedSet = new Set(selection.excludedIds)
      idsToInvite = accessIds.filter((accessId) => !excludedSet.has(accessId))
    } else {
      // "Select all" WITHOUT filters: use all access IDs minus explicitly excluded ones
      // Note: excludedIds from the selector are access IDs (due to useAccessId prop)
      const excludedSet = new Set(selection.excludedIds)
      idsToInvite = accessIds.filter((accessId) => !excludedSet.has(accessId))
    }

    const { success } = await asyncAction(
      async () => {
        await invitationsCollection.rpc('bulk_invite', { accesses_ids: idsToInvite })
      },
      { loadingMessage: i18n.t('employees.pending_invites_modal.loading_message') }
    )
    if (success) handleClose()
  }

  return (
    <Modal
      title={i18n.t('employees.pending_invites_modal.title')}
      description={i18n.t('employees.pending_invites_modal.description')}
      illustration="group"
      onSubmit={handleSubmit}
      onClose={handleClose}
      footer={<Button submit>{i18n.t('employees.pending_invites_modal.submit')}</Button>}
    >
      {loading ? null : (
        <EmployeeSelectorV2
          useAccessId
          forcedFilters={{ employeeIds: employees.map((e) => e.id) }}
          onChange={setSelection}
          multiple
          options={{ disableSelectAll: true }}
        />
      )}
    </Modal>
  )
}

export default PendingModal
